/* ──────────────────────────────────────────────────────────────────────
 * Revenue Trajectory — full-width stacked area chart
 *
 * Three layers (Weddings · Corporate · Birthdays) in champagne / bronze
 * / sage. Built on Recharts so we get hover scrubbing + responsive
 * resizing for free, but every default colour, gridline, and tooltip
 * is overridden so it reads as a luxury editorial chart rather than
 * the stock Recharts demo.
 *
 * Range toggle (1M · 3M · 12M) lives at the top-right. The huge serif
 * total + YoY delta live at the top-left below the title eyebrow.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useMemo, useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot,
} from "recharts";

const CHAMPAGNE     = "#C9A961";
const CHAMPAGNE_HI  = "#E5C77E";
const BRONZE        = "#A87C4F";
const SAGE          = "#7A9B76";
const INK           = "#0F172A";
const SLATE         = "#64748b";
const HAIRLINE      = "rgba(10,14,26,0.06)";
const FONT_SERIF    = `'Fraunces', 'GT Sectra', Georgia, serif`;
const FONT_MONO     = `'JetBrains Mono', 'SF Mono', Menlo, monospace`;

interface Slice { month: string; weddings: number; corporate: number; birthdays: number; annotation?: string; }
interface Props { data: Slice[]; }

type Range = "1M" | "3M" | "12M";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};

export default function RevenueTrajectory({ data }: Props) {
  const [range, setRange] = useState<Range>("12M");

  // Slice the dataset by the active range. 1M = last 1 month, 3M last
  // 3, 12M = everything we have.
  const visible = useMemo(() => {
    if (range === "1M")  return data.slice(-1);
    if (range === "3M")  return data.slice(-3);
    return data;
  }, [data, range]);

  const totals = visible.map((d) => d.weddings + d.corporate + d.birthdays);
  const total  = totals.reduce((s, v) => s + v, 0);

  // YoY delta — compare last 12mo total to previous 12mo if we have
  // 24 months; otherwise compare last point to first point of the
  // visible range for a reasonable directional read.
  const yoy = useMemo(() => {
    if (visible.length < 2) return 0;
    const prev = totals[0];
    const cur  = totals[totals.length - 1];
    if (prev === 0) return 0;
    return Math.round(((cur - prev) / prev) * 100);
  }, [visible, totals]);

  // Peak month annotations — top 2 by total revenue.
  const peakDots = useMemo(() => {
    const indexed = visible.map((d, i) => ({ i, d, total: d.weddings + d.corporate + d.birthdays }));
    return indexed.sort((a, b) => b.total - a.total).slice(0, 2);
  }, [visible]);

  return (
    <Box>
      {/* ── Header strip ─────────────────────────────────────────── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "flex-end" }} justifyContent="space-between" gap={2} mb={2}>
        <Box>
          <Typography sx={{
            fontSize: 10, fontWeight: 600, letterSpacing: "0.22em",
            color: "#A8893E", textTransform: "uppercase",
          }}>
            Revenue Trajectory
          </Typography>
          <Stack direction="row" alignItems="baseline" gap={1.2} mt={0.6}>
            <Typography sx={{
              fontFamily: FONT_SERIF, fontSize: 44, fontWeight: 300,
              color: INK, lineHeight: 0.95, letterSpacing: "-0.025em",
              fontVariantNumeric: "tabular-nums",
            }}>
              {fmtMoney(total)}
            </Typography>
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 0.4,
              fontSize: 11, fontWeight: 700,
              color: yoy >= 0 ? SAGE : "#8B3A3A",
              bgcolor: yoy >= 0 ? "rgba(122,155,118,0.12)" : "rgba(139,58,58,0.12)",
              border: `1px solid ${yoy >= 0 ? "rgba(122,155,118,0.3)" : "rgba(139,58,58,0.3)"}`,
              borderRadius: "999px",
              px: 1, py: 0.3,
              letterSpacing: "0.02em",
            }}>
              {yoy >= 0 ? "↑" : "↓"} {Math.abs(yoy)}% YoY
            </Box>
          </Stack>
          <Typography sx={{
            fontFamily: FONT_SERIF, fontStyle: "italic",
            fontSize: 12, color: SLATE, mt: 0.6,
          }}>
            Booked revenue · last 12 months
          </Typography>
        </Box>

        {/* Range toggle — three pills */}
        <Stack direction="row" gap={0.4} sx={{
          bgcolor: "#fafbfc",
          border: `1px solid ${HAIRLINE}`,
          borderRadius: "999px",
          p: 0.4,
        }}>
          {(["1M", "3M", "12M"] as Range[]).map((r) => {
            const active = range === r;
            return (
              <Box
                key={r}
                role="button"
                onClick={() => setRange(r)}
                sx={{
                  cursor: "pointer", userSelect: "none",
                  px: 1.4, py: 0.4, borderRadius: "999px",
                  fontFamily: FONT_MONO,
                  fontSize: 10.5, fontWeight: 600,
                  letterSpacing: "0.08em",
                  color: active ? "#ffffff" : SLATE,
                  bgcolor: active ? INK : "transparent",
                  transition: "all 200ms cubic-bezier(0.16, 1, 0.3, 1)",
                  "&:hover": active ? {} : { color: INK },
                }}
              >
                {r}
              </Box>
            );
          })}
        </Stack>
      </Stack>

      {/* ── The chart ────────────────────────────────────────────── */}
      <Box sx={{ width: "100%", height: 240, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={visible} margin={{ top: 8, right: 12, left: 0, bottom: 12 }}>
            <defs>
              <linearGradient id="rev-grad-weddings" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={CHAMPAGNE_HI} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHAMPAGNE} stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="rev-grad-corporate" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={BRONZE} stopOpacity={0.4} />
                <stop offset="100%" stopColor={BRONZE} stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="rev-grad-birthdays" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={SAGE} stopOpacity={0.4} />
                <stop offset="100%" stopColor={SAGE} stopOpacity={0.05} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 9.5, fill: SLATE, fontFamily: FONT_MONO, letterSpacing: 1 }}
              dy={4}
            />
            <YAxis hide />

            <Tooltip
              cursor={{ stroke: INK, strokeDasharray: "3 4", strokeOpacity: 0.25, strokeWidth: 1 }}
              content={({ active, payload, label }) => {
                if (!active || !payload || !payload.length) return null;
                const total = payload.reduce((s: number, p: any) => s + (p.value || 0), 0);
                return (
                  <Box sx={{
                    bgcolor: "rgba(255,255,255,0.94)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: `1px solid ${HAIRLINE}`,
                    boxShadow: "0 4px 18px rgba(10,14,26,0.08)",
                    borderRadius: "12px",
                    px: 1.6, py: 1.2, minWidth: 160,
                  }}>
                    <Typography sx={{
                      fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 500,
                      color: SLATE, letterSpacing: "0.12em", textTransform: "uppercase", mb: 0.6,
                    }}>
                      {label}
                    </Typography>
                    {payload.map((p: any) => (
                      <Stack key={p.dataKey} direction="row" alignItems="center" gap={0.7} mt={0.3}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />
                        <Typography sx={{ fontSize: 11, color: INK, flex: 1, textTransform: "capitalize" }}>
                          {p.dataKey}
                        </Typography>
                        <Typography sx={{
                          fontFamily: FONT_SERIF, fontSize: 13, color: INK,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          {fmtMoney(p.value)}
                        </Typography>
                      </Stack>
                    ))}
                    <Box sx={{ borderTop: `1px solid ${HAIRLINE}`, mt: 0.8, pt: 0.5 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ fontSize: 10, color: SLATE, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          Total
                        </Typography>
                        <Typography sx={{
                          fontFamily: FONT_SERIF, fontSize: 14, color: INK,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          {fmtMoney(total)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                );
              }}
            />

            {/* Stack order: corporate (bronze) on bottom, then sage,
                then champagne on top — visually the gold sits at the
                summit, calling the eye. */}
            <Area
              type="monotone" dataKey="corporate"
              stackId="1"
              stroke={BRONZE} strokeWidth={2}
              fill="url(#rev-grad-corporate)"
              isAnimationActive
              animationBegin={400}
              animationDuration={700}
              animationEasing="ease-out"
            />
            <Area
              type="monotone" dataKey="birthdays"
              stackId="1"
              stroke={SAGE} strokeWidth={2}
              fill="url(#rev-grad-birthdays)"
              isAnimationActive
              animationBegin={200}
              animationDuration={700}
              animationEasing="ease-out"
            />
            <Area
              type="monotone" dataKey="weddings"
              stackId="1"
              stroke={CHAMPAGNE} strokeWidth={2.2}
              fill="url(#rev-grad-weddings)"
              isAnimationActive
              animationBegin={0}
              animationDuration={700}
              animationEasing="ease-out"
            />

            {/* Peak annotations — tiny dots above the curve */}
            {peakDots.map((p) => (
              <ReferenceDot
                key={p.i}
                x={p.d.month}
                y={p.total}
                r={3}
                fill={CHAMPAGNE}
                stroke="#ffffff"
                strokeWidth={1.5}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>

        {/* Peak-month italic serif label, floating absolute above
            the chart at the first peak. Recharts doesn't ship custom
            annotation labels nicely, so we render this as plain HTML
            positioned by the index. */}
        {peakDots[0] && (
          <Typography sx={{
            position: "absolute",
            top: 8,
            left: `${5 + (peakDots[0].i / Math.max(1, visible.length - 1)) * 90}%`,
            transform: "translateX(-50%)",
            fontFamily: FONT_SERIF, fontStyle: "italic",
            fontSize: 10.5, color: "#A8893E",
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}>
            Peak season →
          </Typography>
        )}
      </Box>

      {/* Legend row */}
      <Stack direction="row" gap={2.5} mt={1.5} flexWrap="wrap">
        {[
          { key: "Weddings",  color: CHAMPAGNE },
          { key: "Corporate", color: BRONZE },
          { key: "Birthdays", color: SAGE },
        ].map((s) => (
          <Stack key={s.key} direction="row" alignItems="center" gap={0.6}>
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: s.color, boxShadow: `0 0 6px ${s.color}66` }} />
            <Typography sx={{
              fontSize: 10, fontWeight: 600, color: SLATE,
              letterSpacing: "0.12em", textTransform: "uppercase",
            }}>
              {s.key}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/* buildRevenueTrajectoryMock() was removed in the mock-data sweep.
 * Callers must pass real Slice[] derived from confirmed bookings —
 * see DCCDashboard's `revenueTrajectoryData` for the canonical
 * grouping by event-month + event-type. */
